import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { User, MapPin, Phone, Mail, Users, LogOut, Globe, FormInput, Camera, Loader2, Copy } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import type { Profile } from '@/types/database';
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { useLanguage, languageNames, type SupportedLanguage } from '@/hooks/useLanguage';
import { AvatarCropper } from '@/components/AvatarCropper';

import { AppSidebar } from "@/components/AppSidebar";
import { Link } from "react-router-dom";

const Profile = () => {
  const { user, signOut } = useAuth();
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailEditing, setEmailEditing] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  // Initialize sidebar state from localStorage, default to true (expanded)
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarState');
    return saved ? JSON.parse(saved) : true;
  });

  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarState', JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);
  const [profile, setProfile] = useState<Partial<Profile>>({
    first_name: '',
    last_name: '',
    address: '',
    role: 'standard'
  });
  const [customFields, setCustomFields] = useState<Array<{
    id: string;
    field_name: string;
    field_type: string;
    is_required: boolean;
    placeholder: string | null;
  }>>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Avatar cropper state
  const [cropperOpen, setCropperOpen] = useState(false);
  const [selectedImageSrc, setSelectedImageSrc] = useState('');

  useEffect(() => {
    console.log('Profile component mounted, user:', user);
    if (user) {
      console.log('User available, fetching profile...');
      fetchProfile();
      // Initialize email for editing
      if (user?.email) {
        setNewEmail(user.email);
      }
    } else {
      console.log('No user found, setting loading to false');
      setLoading(false);
    }
  }, [user]);

  const fetchProfile = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching profile:', error);
        toast.error('Fehler beim Laden des Profils');
        return;
      }

      if (data) {
        setProfile(data);
        
        // Fetch custom fields for the user's team
        if (data.team_id) {
          await fetchCustomFields(data.team_id);
          await fetchCustomFieldValues();
        }
      } else {
        // Falls kein Profil-Eintrag existiert, verwende user_metadata
        const userMeta = user.user_metadata;
        setProfile({
          first_name: userMeta?.first_name || '',
          last_name: userMeta?.last_name || '',
          address: userMeta?.address || '',
          role: 'standard'
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Fehler beim Laden des Profils');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomFields = async (teamId: string) => {
    try {
      const { data, error } = await supabase
        .from('team_custom_profile_fields')
        .select('*')
        .eq('team_id', teamId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setCustomFields(data || []);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    }
  };

  const fetchCustomFieldValues = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('team_member_custom_field_values')
        .select('field_id, value')
        .eq('user_id', user.id);

      if (error) throw error;
      
      const values: Record<string, string> = {};
      (data || []).forEach(v => {
        values[v.field_id] = v.value || '';
      });
      setCustomFieldValues(values);
    } catch (error) {
      console.error('Error fetching custom field values:', error);
    }
  };

  const saveProfile = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({
          user_id: user.id,
          ...profile,
          role: 'standard', // Rolle ist immer 'standard'
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error saving profile:', error);
        toast.error('Fehler beim Speichern des Profils');
        return;
      }

      // Save custom field values
      for (const field of customFields) {
        const value = customFieldValues[field.id] || '';
        
        const { error: upsertError } = await supabase
          .from('team_member_custom_field_values')
          .upsert({
            field_id: field.id,
            user_id: user.id,
            value: value.trim() || null
          }, { onConflict: 'field_id,user_id' });

        if (upsertError) {
          console.error('Error saving custom field:', upsertError);
        }
      }

      toast.success('Profil erfolgreich gespeichert');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Fehler beim Speichern des Profils');
    } finally {
      setSaving(false);
    }
  };

  const updateProfile = (field: keyof Profile, value: string) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const updateEmail = async () => {
    if (!user || !newEmail || newEmail === user.email) return;

    try {
      // Use production URL for email redirect
      const redirectUrl = window.location.hostname.includes('lovableproject.com') 
        ? 'https://app.d2d-plus.com/' 
        : `${window.location.origin}/`;

      const { error } = await supabase.auth.updateUser(
        { email: newEmail },
        { emailRedirectTo: redirectUrl }
      );

      if (error) {
        console.error('Error updating email:', error);
        
        // Spezifische Fehlermeldungen basierend auf dem Fehlercode
        if (error.message?.includes('already been registered') || (error as any).code === 'email_exists') {
          toast.error('Diese E-Mail-Adresse ist bereits registriert. Bitte verwende eine andere E-Mail-Adresse.');
        } else if (error.message?.includes('invalid email')) {
          toast.error('Ungültige E-Mail-Adresse. Bitte überprüfe die Eingabe.');
        } else {
          toast.error(`Fehler: ${error.message || 'E-Mail konnte nicht geändert werden'}`);
        }
        return;
      }

      toast.success('Bestätigungs-E-Mail gesendet! Bitte prüfe dein Postfach.');
      setEmailEditing(false);
    } catch (error: any) {
      console.error('Error updating email:', error);
      toast.error(error?.message || 'Fehler beim Ändern der E-Mail-Adresse');
    }
  };

  const cancelEmailEdit = () => {
    setNewEmail(user?.email || '');
    setEmailEditing(false);
  };

  // Check if user account was created with email/password (not OAuth)
  const isEmailPasswordAccount = user?.app_metadata?.provider === 'email';

  // Get user initials for avatar fallback
  const getInitials = () => {
    const first = profile.first_name?.charAt(0) || '';
    const last = profile.last_name?.charAt(0) || '';
    return (first + last).toUpperCase() || '?';
  };

  // Handle file selection - open cropper instead of direct upload
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte wähle eine Bilddatei aus');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Das Bild darf maximal 5MB groß sein');
      return;
    }

    // Create object URL and open cropper
    const imageUrl = URL.createObjectURL(file);
    setSelectedImageSrc(imageUrl);
    setCropperOpen(true);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle cropped image upload
  const handleCroppedImage = async (croppedBlob: Blob) => {
    if (!user) return;

    setUploadingAvatar(true);
    try {
      // Create file path: userId/avatar.png (always PNG after cropping)
      const filePath = `${user.id}/avatar.png`;

      // Delete old avatar if exists
      if (profile.avatar_url) {
        const oldPath = profile.avatar_url.split('/').slice(-2).join('/');
        await supabase.storage.from('profile-avatars').remove([oldPath]);
      }

      // Upload new avatar
      const { error: uploadError } = await supabase.storage
        .from('profile-avatars')
        .upload(filePath, croppedBlob, { upsert: true, contentType: 'image/png' });

      if (uploadError) throw uploadError;

      // Get public URL with cache buster
      const { data: { publicUrl } } = supabase.storage
        .from('profile-avatars')
        .getPublicUrl(filePath);

      const urlWithCacheBuster = `${publicUrl}?t=${Date.now()}`;

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: urlWithCacheBuster, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      setProfile(prev => ({ ...prev, avatar_url: urlWithCacheBuster }));
      toast.success('Profilbild aktualisiert');
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Fehler beim Hochladen des Profilbilds');
    } finally {
      setUploadingAvatar(false);
      // Clean up object URL
      if (selectedImageSrc) {
        URL.revokeObjectURL(selectedImageSrc);
        setSelectedImageSrc('');
      }
    }
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
      <div className="min-h-screen flex w-full bg-gradient-subtle">
        {/* Sidebar */}
        <AppSidebar />
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Top Bar */}
          <header className="h-12 flex items-center border-b bg-card/80 backdrop-blur-sm sticky top-0 z-40">
            <SidebarTrigger className="ml-2" />
            <div className="flex-1 flex items-center justify-between px-4">
              <div>
                <h1 className="text-lg font-bold">Mein Profil</h1>
              </div>
            </div>
          </header>

          {/* Content Area */}
          <div className="flex-1 overflow-auto">
            <div className="container mx-auto p-6 max-w-4xl">
              <div className="mb-6">
                <p className="text-muted-foreground mt-2">
                  Verwalte deine persönlichen Informationen und Teamzuordnungen
                </p>
              </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Persönliche Daten
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Avatar Upload */}
            <div className="flex items-center gap-4">
              <div className="relative">
                <Avatar className="h-20 w-20 border-2 border-border">
                  <AvatarImage src={profile.avatar_url} alt="Profilbild" />
                  <AvatarFallback className="text-lg bg-primary/10 text-primary">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
                <Button
                  size="icon"
                  variant="secondary"
                  className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                >
                  {uploadingAvatar ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Camera className="h-3 w-3" />
                  )}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </div>
              <div className="text-sm text-muted-foreground">
                <p>Profilbild hochladen</p>
                <p className="text-xs">Max. 5MB, JPG/PNG</p>
              </div>
            </div>

            {/* Avatar Cropper Dialog */}
            <AvatarCropper
              open={cropperOpen}
              onOpenChange={(open) => {
                setCropperOpen(open);
                if (!open && selectedImageSrc) {
                  URL.revokeObjectURL(selectedImageSrc);
                  setSelectedImageSrc('');
                }
              }}
              imageSrc={selectedImageSrc}
              onCropComplete={handleCroppedImage}
            />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">Vorname</Label>
                <Input
                  id="first_name"
                  value={profile.first_name || ''}
                  onChange={(e) => updateProfile('first_name', e.target.value)}
                  placeholder="Max"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Nachname</Label>
                <Input
                  id="last_name"
                  value={profile.last_name || ''}
                  onChange={(e) => updateProfile('last_name', e.target.value)}
                  placeholder="Mustermann"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                E-Mail-Adresse
                {isEmailPasswordAccount && !emailEditing && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEmailEditing(true)}
                    className="h-6 px-2 text-xs"
                  >
                    Ändern
                  </Button>
                )}
              </Label>
              
              {emailEditing && isEmailPasswordAccount ? (
                <div className="space-y-2">
                  <Input
                    id="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="neue-email@example.com"
                    type="email"
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={updateEmail}
                      disabled={!newEmail || newEmail === user?.email}
                    >
                      Bestätigen
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={cancelEmailEdit}
                    >
                      Abbrechen
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Eine Bestätigungs-E-Mail wird an die neue Adresse gesendet.
                  </p>
                </div>
              ) : (
                <>
                  <Input
                    id="email"
                    value={user?.email || ''}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {isEmailPasswordAccount 
                      ? 'Klicke auf "Ändern" um die E-Mail-Adresse zu bearbeiten'
                      : 'E-Mail kann nicht geändert werden (OAuth-Account)'
                    }
                  </p>
                </>
              )}
            </div>

            <div>
              <Label htmlFor="address" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Adresse
              </Label>
              <Textarea
                id="address"
                value={profile.address || ''}
                onChange={(e) => updateProfile('address', e.target.value)}
                placeholder="Musterstraße 123, 12345 Musterstadt"
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Telefonnummer
              </Label>
              <Input
                id="phone"
                type="tel"
                value={profile.phone || ''}
                onChange={(e) => updateProfile('phone', e.target.value)}
                placeholder="+49 123 456789"
              />
            </div>

            <div>
              <Label htmlFor="contact_email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Kontakt-E-Mail
              </Label>
              <Input
                id="contact_email"
                type="email"
                value={profile.contact_email || ''}
                onChange={(e) => updateProfile('contact_email', e.target.value)}
                placeholder="kontakt@example.com"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Diese E-Mail wird in Kunden-E-Mails als Kontaktadresse angezeigt. Wenn leer, wird deine Anmelde-E-Mail verwendet.
              </p>
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5 flex-1 mr-3">
                <Label htmlFor="send_copy" className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <Copy className="h-4 w-4" />
                  E-Mail-Kopie an Account
                </Label>
                <p className="text-xs text-muted-foreground">
                  Sendet eine Kopie aller ausgehenden E-Mails an deine Account-E-Mail ({user?.email}), wenn diese von der Versand-E-Mail abweicht.
                </p>
              </div>
              <Switch
                id="send_copy"
                checked={(profile as any).send_copy_to_account_email ?? true}
                onCheckedChange={(checked) => setProfile(prev => ({ ...prev, send_copy_to_account_email: checked }))}
              />
            </div>

            <div>
              <Label htmlFor="language" className="flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Sprache
              </Label>
              <Select
                value={language}
                onValueChange={(value: SupportedLanguage) => setLanguage(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sprache auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(languageNames).map(([code, name]) => (
                    <SelectItem key={code} value={code}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Beeinflusst die Anzeige von Datum, Wochentagen und anderen lokalen Elementen
              </p>
            </div>
          </CardContent>
        </Card>

        {/* System Information & Team Fields - Compact */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4" />
              System & Team
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Team Custom Fields inline */}
            {customFields.map((field) => (
              <div key={field.id}>
                <Label htmlFor={`custom-${field.id}`} className="flex items-center gap-1 text-sm">
                  {field.field_name}
                  {field.is_required && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id={`custom-${field.id}`}
                  type={field.field_type === 'number' ? 'number' : field.field_type === 'email' ? 'email' : field.field_type === 'date' ? 'date' : 'text'}
                  value={customFieldValues[field.id] || ''}
                  onChange={(e) => setCustomFieldValues({ ...customFieldValues, [field.id]: e.target.value })}
                  placeholder={field.placeholder || `${field.field_name} eingeben...`}
                  className="h-9"
                />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 flex justify-between">
        <Button
          onClick={signOut}
          variant="destructive"
          className="flex items-center gap-2"
        >
          <LogOut className="h-4 w-4" />
          Abmelden
        </Button>
        
        <Button
          onClick={saveProfile}
          disabled={saving}
          variant="secondary"
        >
          {saving ? 'Wird gespeichert...' : 'Profil speichern'}
        </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default Profile;